import React, { useEffect, useState } from "react";
import { Button, Checkbox, DatePicker, Input, Modal, Select } from "antd";
import moment from "moment";
import styled from "styled-components";
import { getTaskRevenuesBetween } from "../services/task.service";
import { EmployeeType } from "../services/task.interface";
import { getBands } from "../../bands/bands.service";
import { BandDto } from "../../bands/band.interface";
import { useAuthContext } from "../../auth/context/useAuthContext";
import { useDownloadLog } from "../../../hooks/useDownloadLog";
import { exportRevenuesToExcel } from "../utils/revenue-excel.util";

const { RangePicker } = DatePicker;

interface Props {
  visible: boolean;
  onClose: () => void;
}

type ContractFilter = "ALL" | EmployeeType;

export function TaskRevenueExportModal({ visible, onClose }: Props) {
  const { hasPermission } = useAuthContext();
  const { handleLog } = useDownloadLog();

  const [range, setRange] = useState<
    [moment.Moment | null, moment.Moment | null] | null
  >(null);
  const [contractType, setContractType] = useState<ContractFilter>("ALL");
  const [bandId, setBandId] = useState<string | undefined>(undefined);
  const [reason, setReason] = useState("");
  const [allOperators, setAllOperators] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [bands, setBands] = useState<BandDto[]>([]);

  useEffect(() => {
    (async () => {
      const r = await getBands();
      if (r) setBands(r);
    })();
  }, []);

  const handleDownload = async () => {
    if (!hasPermission("Download")) return alert("다운로드 권한이 없습니다.");
    if (!range || !range[0] || !range[1])
      return alert("기간을 선택해주세요.");
    if (!reason.trim()) return alert("다운로드 사유를 입력해주세요.");

    const startDate = range[0].format("YYYY-MM-DD");
    const endDate = range[1].format("YYYY-MM-DD");


    setLoading(true);
    try {
      const results = await getTaskRevenuesBetween({
        startDate: startDate,
        endDate: endDate,
        bandId: bandId,
        contractType: contractType === "ALL" ? undefined : contractType,
        allOperators: allOperators || undefined,
      });

      if (!results || results.length === 0) {
        alert("해당 조건의 정산 실적이 없습니다.");
        return;
      }

      const fileName = `정산_${startDate}_${endDate}.xlsx`;
      await handleLog(reason, fileName, results.length);
      exportRevenuesToExcel(results, startDate, endDate, fileName);
      alert("다운로드 완료");
      onClose();
    } catch (e) {
      console.error(e);
      alert("다운로드 실패");
    } finally {
      setLoading(false);
    }
  };

  const Footer = () => (
    <>
      <Button onClick={onClose}>취소</Button>
      <Button
        type="primary"
        loading={loading}
        disabled={!hasPermission("Download")}
        onClick={handleDownload}
      >
        엑셀 다운로드
      </Button>
    </>
  );

  return (
    <Modal
      title="정산 실적 엑셀 다운로드"
      visible={visible}
      onCancel={onClose}
      footer={<Footer />}
      forceRender
    >
      <FieldWrapper>
        <label>기간</label>
        <RangePicker
          value={range as any}
          onChange={(v) => setRange(v as any)}
          format="YYYY-MM-DD"
          allowClear
        />
      </FieldWrapper>
      <FieldWrapper>
        <label>근로 유형</label>
        <Select
          value={contractType}
          onChange={(v) => setContractType(v)}
          style={{ width: 200 }}
        >
          <Select.Option value="ALL">전체</Select.Option>
          <Select.Option value="DM">직원</Select.Option>
          <Select.Option value="OS">지입</Select.Option>
        </Select>
      </FieldWrapper>
      <FieldWrapper>
        <label>밴드 (지역)</label>
        <Select
          value={bandId}
          onChange={(v) => setBandId(v)}
          style={{ width: 240 }}
          allowClear
          showSearch
          placeholder="전체 (미선택 시 운영자 전체)"
          onClear={() => setBandId(undefined)}
          filterOption={(input, option) =>
            (option!.children as unknown as string).includes(input)
          }
        >
          {bands.map((b, i) => (
            <Select.Option key={"band-" + i} value={b.id}>
              {b.name ?? "invalid band name"}
            </Select.Option>
          ))}
        </Select>
      </FieldWrapper>
      <FieldWrapper>
        <label>다운로드 사유</label>
        <Input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="다운로드 사유를 입력해주세요."
          style={{ width: 240 }}
        />
      </FieldWrapper>
      <FieldWrapper>
        <label></label>
        <Checkbox
          checked={allOperators}
          onChange={(e) => setAllOperators(e.target.checked)}
        >
          전체 운영자 (운영자 제한 없이 모든 캠프 콜렉터 포함)
        </Checkbox>
      </FieldWrapper>
    </Modal>
  );
}

const FieldWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;

  & > label {
    min-width: 80px;
    font-weight: 500;
  }
`;
